import axios from 'axios';
import type {
  SilsilahAdminProfileResponse,
  SilsilahAdminTokenResponse,
} from '@/projects/silsilah/models/adminAuth.model';
import type {
  SilsilahAdminUserResponse,
  SilsilahAdminUsersResponse,
  SilsilahAdminUserUpdate,
  SilsilahAdminUserSearchBy,
} from '@/projects/silsilah/models/adminUser.model';
import type {
  SilsilahAdminHierarchyBatak,
  SilsilahAdminHierarchyBatakResponse,
  SilsilahAdminHierarchyBatakDetail,
  SilsilahAdminHierarchyBatakEditPayload,
  SilsilahAdminHierarchyBatakCreatePayload,
} from '@/projects/silsilah/models/adminHierarchyBatak.model';
import type {
  SilsilahAdminContentDetail,
  SilsilahAdminContentPayload,
  SilsilahAdminContentsPage,
} from '@/projects/silsilah/models/adminContent.model';
import type { SilsilahAdminDashboard } from '@/projects/silsilah/models/adminDashboard.model';

const silsilahAdminClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL_SILSILAH,
  timeout: 30000,
});

export const silsilahAdminApiSource = {
  async dashboard(token: string): Promise<SilsilahAdminDashboard> {
    const response = await silsilahAdminClient.get<{ data: SilsilahAdminDashboard }>(
      '/admin/dashboard',
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },

  async login(googleToken: string): Promise<SilsilahAdminTokenResponse> {
    const response = await silsilahAdminClient.post<{ data: SilsilahAdminTokenResponse }>(
      '/admin/login',
      { token: googleToken },
    );
    return response.data.data;
  },

  async profile(token: string): Promise<SilsilahAdminProfileResponse> {
    const response = await silsilahAdminClient.get<{ data: SilsilahAdminProfileResponse }>(
      '/admin/profile',
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },

  async users(
    token: string,
    page: number,
    size: number,
    searchBy?: SilsilahAdminUserSearchBy,
    query?: string,
  ): Promise<SilsilahAdminUsersResponse> {
    const response = await silsilahAdminClient.get<{ data: SilsilahAdminUsersResponse }>(
      '/admin/users',
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, size, ...(query?.trim() ? { searchBy, query: query.trim() } : {}) },
      },
    );
    return response.data.data;
  },
  async updateUser(token: string, id: string, payload: SilsilahAdminUserUpdate) {
    const response = await silsilahAdminClient.put<{ data: SilsilahAdminUserResponse }>(
      `/admin/users/${id}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
  async deleteUser(token: string, id: string): Promise<void> {
    await silsilahAdminClient.delete(`/admin/users/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  async hierarchyBatak(
    token: string,
    page: number,
    size: number,
    query: string,
  ): Promise<SilsilahAdminHierarchyBatakResponse> {
    const response = await silsilahAdminClient.get<{
      data: SilsilahAdminHierarchyBatakResponse;
    }>('/admin/hierarchy-batak', {
      headers: { Authorization: `Bearer ${token}` },
      params: { page, size, query: query || undefined },
    });
    return response.data.data;
  },
  async hierarchyBatakParentOptions(token: string): Promise<SilsilahAdminHierarchyBatak[]> {
    const response = await silsilahAdminClient.get<{
      data: SilsilahAdminHierarchyBatak[];
    }>('/admin/hierarchy-batak/parent-options', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },
  async hierarchyBatakDetail(
    token: string,
    id: string,
  ): Promise<SilsilahAdminHierarchyBatakDetail> {
    const response = await silsilahAdminClient.get<{
      data: SilsilahAdminHierarchyBatakDetail;
    }>(`/admin/hierarchy-batak/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },
  async updateHierarchyBatak(
    token: string,
    id: string,
    payload: SilsilahAdminHierarchyBatakEditPayload,
  ): Promise<SilsilahAdminHierarchyBatakDetail> {
    const response = await silsilahAdminClient.put<{
      data: SilsilahAdminHierarchyBatakDetail;
    }>(`/admin/hierarchy-batak/${id}`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  },
  async updateHierarchyBatakParent(
    token: string,
    id: string,
    parentIdKey: string,
  ): Promise<SilsilahAdminHierarchyBatak> {
    const response = await silsilahAdminClient.put<{ data: SilsilahAdminHierarchyBatak }>(
      `/admin/hierarchy-batak/${id}/parent`,
      { parentIdKey },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
  async createHierarchyBatak(
    token: string,
    payload: SilsilahAdminHierarchyBatakCreatePayload,
  ): Promise<SilsilahAdminHierarchyBatak> {
    const response = await silsilahAdminClient.post<{ data: SilsilahAdminHierarchyBatak }>(
      '/admin/hierarchy-batak',
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
  async reorderHierarchyBatakChildren(
    token: string,
    parentIdKey: string,
    items: { id: string; position: number }[],
  ): Promise<SilsilahAdminHierarchyBatak[]> {
    const response = await silsilahAdminClient.put<{
      data: SilsilahAdminHierarchyBatak[];
    }>(
      `/admin/hierarchy-batak/${encodeURIComponent(parentIdKey)}/children-order`,
      { items },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
  async deleteHierarchyBatak(token: string, id: string): Promise<void> {
    await silsilahAdminClient.delete(`/admin/hierarchy-batak/${encodeURIComponent(id)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
  async contents(token: string, page: number, size: number, userId?: string) {
    const response = await silsilahAdminClient.get<{ data: SilsilahAdminContentsPage }>(
      '/admin/contents',
      { headers: { Authorization: `Bearer ${token}` }, params: { page, size, userId } },
    );
    return response.data.data;
  },
  async content(token: string, id: string) {
    const response = await silsilahAdminClient.get<{ data: SilsilahAdminContentDetail }>(
      `/admin/contents/${id}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
  async createContent(token: string, payload: SilsilahAdminContentPayload) {
    const response = await silsilahAdminClient.post<{ data: SilsilahAdminContentDetail }>(
      '/admin/contents',
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
  async updateContent(token: string, id: string, payload: SilsilahAdminContentPayload) {
    const response = await silsilahAdminClient.put<{ data: SilsilahAdminContentDetail }>(
      `/admin/contents/${id}`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data.data;
  },
};
